import {
  Bell,
  BookOpen,
  Bot,
  BriefcaseBusiness,
  GraduationCap,
  Home,
  Lightbulb,
  Menu,
  Search,
  ShoppingBag,
  Store,
  UserRound,
  X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useCity } from "../city";
import { useCart } from "../store";
import { DirectDirectory } from "../world/DirectDirectory";
import { DistrictHUD } from "../world/DistrictHUD";
import { districtForPath } from "../world/districts";
import { RouteAnnouncer } from "../world/RouteAnnouncer";
import { WorldBoundary } from "../world/WorldBoundary";
import { WorldControls } from "../world/WorldControls";
import { WorldTravelLayer } from "../world/WorldTravelLayer";
import { useWorldExperience } from "../world/WorldExperienceProvider";

const nav = [
  { to: "/", label: "Garden", icon: Home },
  { to: "/market", label: "Market", icon: Store },
  { to: "/library", label: "Library", icon: BookOpen },
  { to: "/services", label: "Services", icon: BriefcaseBusiness },
  { to: "/academy", label: "Academy", icon: GraduationCap },
  { to: "/insights", label: "Insights", icon: Lightbulb },
  { to: "/ai", label: "AI", icon: Bot },
];

export function Shell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const district = districtForPath(pathname);
  const { count } = useCart();
  const { mode: cityMode, refresh, user, inboxUnread } = useCity();
  const { mode: worldMode } = useWorldExperience();
  const home = pathname === "/";

  return (
    <div
      className="site-shell"
      data-district={district.districtId}
      data-home={home ? "true" : undefined}
      data-world-mode={worldMode}
    >
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <RouteAnnouncer />
      <WorldTravelLayer />
      <header className="topbar">
        <Link className="brand" to="/" aria-label="Digital Garden City home">
          <span className="brand-mark">✦</span>
          <span>
            <strong>Your Name Village</strong>
            <small>Digital Garden City</small>
          </span>
        </Link>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {nav.map(({ to, label }) => (
            <NavLink key={to} to={to} end={to === "/"}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="top-actions">
          <button className="icon-button" aria-label="Search the city">
            <Search size={18} />
          </button>
          <Link
            className="icon-button badge-wrap"
            to="/inbox"
            aria-label={
              user
                ? `City Inbox with ${inboxUnread} unread items`
                : "City Inbox"
            }
          >
            <Bell size={18} />
            {user && inboxUnread > 0 && (
              <span className="badge">
                {inboxUnread > 99 ? "99+" : inboxUnread}
              </span>
            )}
          </Link>
          <Link
            className="icon-button badge-wrap"
            to="/cart"
            aria-label={`Cart with ${count} items`}
          >
            <ShoppingBag size={18} />
            {count > 0 && <span className="badge">{count}</span>}
          </Link>
          <Link className="profile-button" to="/profile">
            <UserRound size={17} />
            <span>My Space</span>
          </Link>
          <button
            className="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </header>
      {open && (
        <nav className="mobile-menu" aria-label="Mobile menu">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} onClick={() => setOpen(false)}>
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>
      )}
      {cityMode !== "live" && (
        <div
          className={
            cityMode === "loading" ? "city-status loading" : "city-status fallback"
          }
        >
          <span>
            {cityMode === "loading"
              ? "Connecting to the city API…"
              : "Offline catalogue mode — live account and checkout data are unavailable."}
          </span>
          {cityMode === "fallback" && (
            <button onClick={() => void refresh()}>Retry connection</button>
          )}
        </div>
      )}
      <WorldBoundary />
      <DistrictHUD />
      <main id="main-content" tabIndex={-1}>
        {children}
      </main>
      <footer className="footer">
        <div>
          <span className="brand-mark small">✦</span>
          <strong>Your Name Village</strong>
          <p>
            An anime-realistic digital garden world for original software,
            knowledge and intelligent systems.
          </p>
        </div>
        <div className="footer-links">
          <Link to="/market">Market</Link>
          <Link to="/library">Library</Link>
          <Link to="/services">Services</Link>
          <Link to="/academy">Academy</Link>
          <Link to="/inbox">Inbox</Link>
          <Link to="/certificate">Verify certificate</Link>
          <Link to="/ai">Personal AI</Link>
          <Link to="/profile">Support</Link>
        </div>
        <div>
          <WorldControls />
          <a href="#direct-directory">Open Direct Directory</a>
          <small>© 2026 Sami. All rights reserved.</small>
          <small>Built with a zero-monthly-cost starting architecture.</small>
        </div>
      </footer>
      <DirectDirectory />
      <nav className="bottom-nav" aria-label="Mobile primary navigation">
        {nav
          .filter((item) => ["/", "/market", "/services"].includes(item.to))
          .map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"}>
              <Icon size={20} />
              <span>{label}</span>
            </NavLink>
          ))}
        <NavLink to="/cart">
          <ShoppingBag size={20} />
          <span>Cart</span>
          {count > 0 && <b>{count}</b>}
        </NavLink>
        <NavLink to="/profile">
          <UserRound size={20} />
          <span>Profile</span>
        </NavLink>
      </nav>
    </div>
  );
}
